import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { localDateString } from '../../../utils/helpers/localDate';
import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    getMyAttendancePunches,
    getMyAttendanceSummary,
    getAttendanceEnums,
} from '../../../redux/actions/SocietyPortal/AttendanceAction/AttendanceAction';
import { PaginationLimit } from '../../../components/PaginationLimit';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_PUNCH_ATTENDANCE } from '../../../Routing/routes';

const toDateInput = (d) => localDateString(d);

const currentMonthRange = () => {
    const now = new Date();
    return {
        from: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1, 12)),
        to: toDateInput(now),
    };
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-IN', {
        weekday: 'short', day: '2-digit', month: 'short',
    });
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
};

const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const prettyEnum = (v) => (v || '').replace(/_/g, ' ');

const DAY_STATUS_STYLES = {
    PRESENT: 'tw-bg-emerald-50 tw-text-emerald-700',
    HALF_DAY: 'tw-bg-amber-50 tw-text-amber-700',
    INCOMPLETE: 'tw-bg-orange-50 tw-text-orange-700',
    ABSENT: 'tw-bg-gray-100 tw-text-gray-500',
};

const PUNCH_STATUS_STYLES = {
    CONFIRMED: 'tw-bg-emerald-50 tw-text-emerald-700',
    PENDING_APPROVAL: 'tw-bg-blue-50 tw-text-blue-700',
    REJECTED: 'tw-bg-red-50 tw-text-red-700',
};

// Work log rows are submitted with — and returned on — the OUT punch.
const WORK_LOG_STATUS_STYLES = {
    COMPLETED: 'tw-bg-emerald-50 tw-text-emerald-700',
    IN_PROGRESS: 'tw-bg-blue-50 tw-text-blue-700',
    PENDING: 'tw-bg-amber-50 tw-text-amber-700',
};

const Badge = ({ value, styles }) => (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${styles[value] || 'tw-bg-gray-100 tw-text-gray-500'}`}>
        {prettyEnum(value) || '—'}
    </span>
);

// While the shift is still open the stored status is INCOMPLETE by design — it only
// settles to Present/Half Day once you punch out and the worked minutes are known.
// Showing "Incomplete" mid-shift reads as a fault, so an open day says so instead.
const DayStatusBadge = ({ day }) => {
    if (day.current_state === 'IN') {
        return (
            <span
                className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-sky-50 tw-text-sky-700"
                title="Punched in — the day is still open. It will be finalised when you punch out."
            >
                <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-sky-500 tw-animate-pulse" />
                In progress
            </span>
        );
    }
    return <Badge value={day.day_status} styles={DAY_STATUS_STYLES} />;
};

const punchLocation = (p) => {
    if (p.location_name) return p.location_name;
    if (p.geocode_status === 'PENDING') return 'Resolving location…';
    if (p.latitude != null && p.longitude != null) {
        return `${Number(p.latitude).toFixed(5)}, ${Number(p.longitude).toFixed(5)}`;
    }
    return '—';
};

const MyAttendance = () => {
    const dispatch = useDispatch();
    const {
        mySummaryDays,
        mySummaryTotals,
        mySummaryLoading,
        myPunches,
        myPunchesPagination,
        myPunchesLoading,
        attendanceEnums,
    } = useSelector(state => state.AttendanceReducer || {});

    const [tab, setTab] = useState('summary'); // summary | punches
    const [range, setRange] = useState(currentMonthRange);
    const [punchType, setPunchType] = useState('');
    const [punchStatus, setPunchStatus] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);

    useEffect(() => {
        dispatch(getAttendanceEnums());
    }, [dispatch]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getMyAttendanceSummary({ from: range.from, to: range.to, limit: 100 }));
    }, [dispatch, range.from, range.to, refreshKey]);

    useEffect(() => {
        if (tab !== 'punches') return;
        dispatch(getMyAttendancePunches({
            from: range.from,
            to: range.to,
            punch_type: punchType,
            status: punchStatus,
            page,
            limit,
        }));
    }, [dispatch, tab, range.from, range.to, punchType, punchStatus, page, limit, refreshKey]);

    const handleRangeChange = (name, value) => {
        setRange(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const totals = mySummaryTotals || {};
    const punchesPg = myPunchesPagination || {};

    const statCards = [
        { icon: 'mdi:calendar-check-outline', color: 'tw-bg-emerald-500', label: 'Present days', value: totals.present_days ?? 0 },
        { icon: 'mdi:circle-half-full', color: 'tw-bg-amber-500', label: 'Half days', value: totals.half_days ?? 0 },
        { icon: 'mdi:progress-clock', color: 'tw-bg-orange-500', label: 'Incomplete days', value: totals.incomplete_days ?? 0 },
        { icon: 'mdi:timer-outline', color: 'tw-bg-blue-500', label: 'Hours worked', value: totals.worked_display || '0h 00m' },
        { icon: 'mdi:coffee-outline', color: 'tw-bg-purple-500', label: 'Break time', value: totals.break_display || '0h 00m' },
    ];

    const dayColumns = ['Date', 'First in', 'Last out', 'Worked', 'Break', 'Punches', 'Status'];
    const punchColumns = ['Date & Time', 'Type', 'Location', 'Distance', 'Geofence', 'Status', 'Work log', 'Source'];

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">My Attendance</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Your daily attendance and punch history</p>
                </div>
                <Link
                    to={SOCIETY_PORTAL + SOCIETY_PORTAL_PUNCH_ATTENDANCE}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-emerald-700 tw-border tw-border-emerald-200 tw-rounded-md hover:tw-bg-emerald-50"
                >
                    <Icon icon="mdi:gesture-tap-button" /> Punch in / out
                </Link>
            </div>

            {/* Period totals */}
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3 tw-mb-4">
                {statCards.map(card => (
                    <div key={card.label} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-100 tw-p-3 tw-flex tw-items-center tw-gap-2.5">
                        <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${card.color}`}>
                            <Icon icon={card.icon} className="tw-text-white tw-text-base" />
                        </div>
                        <div className="tw-min-w-0">
                            <div className="tw-text-base tw-font-bold tw-text-gray-800 tw-leading-tight">{card.value}</div>
                            <div className="tw-text-[10px] tw-text-gray-500 tw-truncate">{card.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">From</label>
                        <input
                            type="date"
                            value={range.from}
                            onChange={(e) => handleRangeChange('from', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">To</label>
                        <input
                            type="date"
                            value={range.to}
                            onChange={(e) => handleRangeChange('to', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>
                    {tab === 'punches' && (
                        <>
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Punch type</label>
                                <select
                                    value={punchType}
                                    onChange={(e) => { setPunchType(e.target.value); setPage(1); }}
                                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                >
                                    <option value="">All</option>
                                    {(attendanceEnums?.punch_types || []).map(v => (
                                        <option key={v} value={v}>{prettyEnum(v)}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                                <select
                                    value={punchStatus}
                                    onChange={(e) => { setPunchStatus(e.target.value); setPage(1); }}
                                    className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                >
                                    <option value="">All</option>
                                    {(attendanceEnums?.punch_statuses || []).map(v => (
                                        <option key={v} value={v}>{prettyEnum(v)}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tw-flex tw-gap-1 tw-mb-3">
                {[
                    { key: 'summary', label: 'Daily summary', icon: 'mdi:calendar-month-outline' },
                    { key: 'punches', label: 'Punch log', icon: 'mdi:format-list-bulleted' },
                ].map(t => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-border ${tab === t.key
                            ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                            : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'}`}
                    >
                        <Icon icon={t.icon} /> {t.label}
                    </button>
                ))}
            </div>

            {tab === 'summary' ? (
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                    <div className="tw-overflow-x-auto">
                        <table className="tw-w-full tw-text-xs">
                            <thead>
                                <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                    {dayColumns.map(h => (
                                        <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mySummaryLoading ? (
                                    <tr>
                                        <td colSpan={dayColumns.length} className="tw-text-center tw-py-12">
                                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                        </td>
                                    </tr>
                                ) : (mySummaryDays || []).length === 0 ? (
                                    <tr>
                                        <td colSpan={dayColumns.length} className="tw-text-center tw-py-12">
                                            <Icon icon="mdi:calendar-blank-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No attendance in this period</p>
                                        </td>
                                    </tr>
                                ) : (
                                    mySummaryDays.map(d => (
                                        <tr key={d.attendance_day_id || d.attendance_date} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-font-medium tw-text-gray-800">{formatDate(d.attendance_date)}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                                {formatTime(d.first_in_at)}
                                                {d.first_in_location_name ? <div className="tw-text-[10px] tw-text-gray-400">{d.first_in_location_name}</div> : null}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                                {formatTime(d.last_out_at)}
                                                {d.last_out_location_name ? <div className="tw-text-[10px] tw-text-gray-400">{d.last_out_location_name}</div> : null}
                                            </td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{d.total_worked_display || '—'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{d.total_break_display || '—'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-text-gray-600">{d.punch_count ?? '—'}</td>
                                            <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                                <DayStatusBadge day={d} />
                                                {d.has_pending_approval && (
                                                    <span className="tw-ml-1.5 tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-bg-blue-50 tw-text-blue-700" title="One or more punches this day await admin approval">
                                                        Pending approval
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <>
                    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-xs">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                        {punchColumns.map(h => (
                                            <th key={h} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {myPunchesLoading ? (
                                        <tr>
                                            <td colSpan={punchColumns.length} className="tw-text-center tw-py-12">
                                                <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                            </td>
                                        </tr>
                                    ) : (myPunches || []).length === 0 ? (
                                        <tr>
                                            <td colSpan={punchColumns.length} className="tw-text-center tw-py-12">
                                                <Icon icon="mdi:gesture-tap" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No punches in this period</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        myPunches.map(p => (
                                            <tr key={p.punch_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{formatDateTime(p.punch_at)}</td>
                                                <td className="tw-px-3 tw-py-2">
                                                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${p.punch_type === 'IN' ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-rose-50 tw-text-rose-700'}`}>
                                                        <Icon icon={p.punch_type === 'IN' ? 'mdi:login' : 'mdi:logout'} className="tw-text-xs" />
                                                        {p.punch_type}
                                                    </span>
                                                    {p.is_manual && (
                                                        <span className="tw-ml-1 tw-inline-flex tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-bg-gray-100 tw-text-gray-500" title={p.manual_reason || 'Added by an admin'}>Manual</span>
                                                    )}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-max-w-[220px] tw-truncate" title={p.location_address || ''}>{punchLocation(p)}</td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">{p.distance_meters != null ? `${Math.round(p.distance_meters)} m` : '—'}</td>
                                                <td className="tw-px-3 tw-py-2">
                                                    {p.is_within_geofence === true && <span className="tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded tw-bg-emerald-50 tw-text-emerald-700">Inside</span>}
                                                    {p.is_within_geofence === false && <span className="tw-text-[11px] tw-px-2 tw-py-0.5 tw-rounded tw-bg-amber-50 tw-text-amber-700">Outside</span>}
                                                    {p.is_within_geofence == null && <span className="tw-text-[11px] tw-text-gray-400">—</span>}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap"><Badge value={p.status} styles={PUNCH_STATUS_STYLES} /></td>
                                                <td className="tw-px-3 tw-py-2 tw-max-w-[260px]">
                                                    {(p.work_logs || []).length === 0 ? (
                                                        <span className="tw-text-[11px] tw-text-gray-400">—</span>
                                                    ) : (
                                                        <div className="tw-flex tw-flex-col tw-gap-1">
                                                            {p.work_logs.map(w => (
                                                                <div key={w.work_log_id} title={w.description}>
                                                                    <div className="tw-flex tw-items-center tw-gap-1.5">
                                                                        <span className="tw-text-[11px] tw-font-medium tw-text-gray-700">{w.work_type}</span>
                                                                        <span className={`tw-text-[9px] tw-px-1.5 tw-py-px tw-rounded tw-whitespace-nowrap ${WORK_LOG_STATUS_STYLES[w.status] || 'tw-bg-gray-100 tw-text-gray-500'}`}>
                                                                            {prettyEnum(w.status).toLowerCase()}
                                                                        </span>
                                                                    </div>
                                                                    <div className="tw-text-[10px] tw-text-gray-400 tw-truncate">{w.description}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="tw-px-3 tw-py-2 tw-text-gray-500">{p.source || '—'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {punchesPg.total > 0 && (
                        <PaginationLimit
                            currentPage={page}
                            totalRecords={punchesPg.total || 0}
                            limit={limit}
                            handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                            handlePageChange={(p) => setPage(p)}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default MyAttendance;
